import React, { createContext, useContext, useState } from "react";

/**
 * Multi-step state shared across the applicant + voucher flows
 * (current step, connected sources, resume gate). Previously misnamed
 * `DemoContext`; this version ships with no seeded persona data.
 */

export interface ConnectedSource {
  id: string;
  category: "social" | "financial" | "other";
  name: string;
  tenure: string;
  corroboration: string;
  strength: "strong" | "moderate" | "limited";
}

export type ProviderConnectionStatus = "connected" | "reconnect_required" | "not_completed";

export interface ConnectedProvider {
  provider: string;
  maskedEmail: string;
  status: ProviderConnectionStatus;
  connectedAt?: string;
}

export type ResumePhase = "none" | "verify" | "verifying" | "restoring" | "resumed";

export type FlowMode = "live";

interface FlowState {
  mode: FlowMode;
  sessionToken: string | null;
  stepUpCompleted: boolean;
  voucherCompleted: boolean;
  currentStep: number;
  voucherStep: number;
  connectedSources: ConnectedSource[];
  // Resume flow state
  resumePhase: ResumePhase;
  draftSavedAt: string | null;
  draftExpiresAt: string | null;
  savedStep: number;
  connectedProviders: ConnectedProvider[];
  applicantEmail: string;
}

interface FlowContextType {
  state: FlowState;
  setMode: (mode: FlowMode, token?: string | null) => void;
  completeStepUp: () => void;
  completeVoucher: () => void;
  setCurrentStep: (step: number) => void;
  setVoucherStep: (step: number) => void;
  connectSource: (source: ConnectedSource) => void;
  disconnectSource: (id: string) => void;
  resetApplicantFlow: () => void;
  resetVoucherFlow: () => void;
  resetFlow: () => void;
  // Resume flow actions
  simulateLeaveAndReturn: () => void;
  startResumeVerification: () => void;
  completeResumeVerification: () => void;
  cancelResume: () => void;
  reconnectProvider: (provider: string) => void;
  disconnectProvider: (provider: string) => void;
}

const initialState: FlowState = {
  mode: "live",
  sessionToken: null,
  stepUpCompleted: false,
  voucherCompleted: false,
  currentStep: 0,
  voucherStep: 0,
  connectedSources: [],
  resumePhase: "none",
  draftSavedAt: null,
  draftExpiresAt: null,
  savedStep: 0,
  connectedProviders: [],
  applicantEmail: "",
};

const FlowContext = createContext<FlowContextType | null>(null);

export function FlowProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<FlowState>(initialState);

  const setMode = (mode: FlowMode, token: string | null = null) =>
    setState((s) => ({ ...s, mode, sessionToken: token }));

  const completeStepUp = () =>
    setState((s) => ({ ...s, stepUpCompleted: true, resumePhase: "none" }));

  const completeVoucher = () =>
    setState((s) => ({ ...s, voucherCompleted: true }));

  const setCurrentStep = (step: number) =>
    setState((s) => ({ ...s, currentStep: step }));

  const setVoucherStep = (step: number) =>
    setState((s) => ({ ...s, voucherStep: step }));

  const connectSource = (source: ConnectedSource) =>
    setState((s) => ({
      ...s,
      connectedSources: s.connectedSources.some((c) => c.id === source.id)
        ? s.connectedSources
        : [...s.connectedSources, source],
    }));

  const disconnectSource = (id: string) =>
    setState((s) => ({
      ...s,
      connectedSources: s.connectedSources.filter((c) => c.id !== id),
    }));

  const resetApplicantFlow = () =>
    setState((s) => ({
      ...s,
      stepUpCompleted: false,
      currentStep: 0,
      connectedSources: [],
      resumePhase: "none",
      draftSavedAt: null,
      draftExpiresAt: null,
      savedStep: 0,
      connectedProviders: [],
    }));

  const resetVoucherFlow = () =>
    setState((s) => ({ ...s, voucherCompleted: false, voucherStep: 0 }));

  const resetFlow = () => setState(initialState);

  const simulateLeaveAndReturn = () => {
    const now = new Date();
    const expires = new Date(now.getTime() + 76 * 60 * 60 * 1000);
    setState((s) => ({
      ...s,
      resumePhase: "verify",
      draftSavedAt: now.toISOString(),
      draftExpiresAt: expires.toISOString(),
      savedStep: s.currentStep,
      connectedProviders: [],
    }));
  };

  const startResumeVerification = () =>
    setState((s) => ({ ...s, resumePhase: "verifying" }));

  const completeResumeVerification = () => {
    setState((s) => ({ ...s, resumePhase: "restoring" }));
    setTimeout(() => {
      setState((s) => ({ ...s, resumePhase: "resumed", currentStep: s.savedStep }));
    }, 1500);
  };

  const cancelResume = () =>
    setState((s) => ({
      ...s,
      resumePhase: "none",
      draftSavedAt: null,
      draftExpiresAt: null,
      savedStep: 0,
      connectedProviders: [],
    }));

  const reconnectProvider = (provider: string) =>
    setState((s) => ({
      ...s,
      connectedProviders: s.connectedProviders.map((p) =>
        p.provider === provider ? { ...p, status: "connected" as const } : p
      ),
    }));

  const disconnectProvider = (provider: string) =>
    setState((s) => ({
      ...s,
      connectedProviders: s.connectedProviders.filter((p) => p.provider !== provider),
    }));

  return (
    <FlowContext.Provider
      value={{
        state, setMode, completeStepUp, completeVoucher, setCurrentStep, setVoucherStep,
        connectSource, disconnectSource, resetApplicantFlow, resetVoucherFlow, resetFlow,
        simulateLeaveAndReturn, startResumeVerification, completeResumeVerification,
        cancelResume, reconnectProvider, disconnectProvider,
      }}
    >
      {children}
    </FlowContext.Provider>
  );
}

export const useFlow = () => {
  const ctx = useContext(FlowContext);
  if (!ctx) throw new Error("useFlow must be used within FlowProvider");
  return ctx;
};
